/**
 * This file contains React Query hook wrappers generated to maintain
 * compatibility with the old @7nohe/openapi-react-query-codegen API.
 *
 * As the `throwOnError` default has changed between the two codegen
 * versions, the call sites might need to be updated to handle HTTP
 * errors in `onError` callbacks (previously, HTTP errors were on
 * `onSuccess` callbacks).
 *
 * These wrappers adapt the new generated hooks to match the expected
 * signatures and behaviors of the previous codegen version. It has
 * mainly been written by Claude Code.
 */
import {
  useQuery,
  useMutation,
  useSuspenseQuery,
  UseQueryOptions,
  UseMutationOptions,
  UseSuspenseQueryOptions,
} from "@tanstack/react-query";

import {
  getAgenciesGeographiesAgenciesGetOptions,
  getAgenciesGeographiesAgenciesGetQueryKey,
  getRegionsGeographiesRegionsGetOptions,
  getRegionsGeographiesRegionsGetQueryKey,
  getZonesGeographiesZonesGetOptions,
  getZonesGeographiesZonesGetQueryKey,
  getLegalEntityLegalEntitiesGetOptions,
  getLegalEntityLegalEntitiesGetQueryKey,
  updateZoneGeographiesZonesZoneUuidPutMutation,
  createZoneGeographiesZonesPostMutation,
  getPostalCodesGeographiesPostalCodesGetOptions,
  getPostalCodesGeographiesPostalCodesGetQueryKey,
  getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetOptions,
  getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetQueryKey,
  upsertPostalCodesBindingGeographiesPostalCodesBindingPutMutation,
  deletePostalCodesBindingGeographiesPostalCodesBindingDeleteMutation,
  previewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostMutation,
  applyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostMutation,
  getExternalProvidersErrorsExternalProvidersErrorsGetOptions,
  getExternalProvidersErrorsExternalProvidersErrorsGetQueryKey,
  getSecurityPolicyPermerSecurityPolicyGetOptions,
  getSecurityPolicyPermerSecurityPolicyGetQueryKey,
} from "@/codegen/openapi2/@tanstack/react-query.gen";
import { type Options } from "@/codegen/openapi2/client/types.gen";

import type {
  GetAgenciesGeographiesAgenciesGetData,
  AgencyType,
  GetRegionsGeographiesRegionsGetData,
  RegionType,
  GetZonesGeographiesZonesGetData,
  ZoneType,
  GetLegalEntityLegalEntitiesGetData,
  LegalEntityType,
  UpdateZoneGeographiesZonesZoneUuidPutData,
  UpdateZoneGeographiesZonesZoneUuidPutError,
  CreateZoneGeographiesZonesPostData,
  CreateZoneGeographiesZonesPostError,
  GetPostalCodesGeographiesPostalCodesGetData,
  PostalCodeType,
  GetPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetData,
  PaginateResourcesPostalCodeWithAgencyType,
  UpsertPostalCodesBindingGeographiesPostalCodesBindingPutData,
  UpsertPostalCodesBindingGeographiesPostalCodesBindingPutError,
  DeletePostalCodesBindingGeographiesPostalCodesBindingDeleteData,
  DeletePostalCodesBindingGeographiesPostalCodesBindingDeleteError,
  PreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostData,
  PreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostError,
  BindingsImportPreviewType,
  ApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostData,
  ApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostError,
  BindingsImportApplyResultType,
  GetExternalProvidersErrorsExternalProvidersErrorsGetData,
  PaginateResourcesExternalProviderErrorType,
  GetSecurityPolicyPermerSecurityPolicyGetData,
} from "@/codegen/openapi2";

/**
 * Hook wrapper for getAgenciesGeographiesAgenciesGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetAgenciesGeographiesAgenciesGet = <TData = AgencyType[], TError = any>(
  clientOptions: Options<GetAgenciesGeographiesAgenciesGetData> = {},
  queryKey?: unknown[],
  options?: Omit<UseQueryOptions<TData, TError, AgencyType[]>, "queryKey" | "queryFn">
) => {
  const baseOptions = getAgenciesGeographiesAgenciesGetOptions(clientOptions);
  return useQuery<TData, TError, AgencyType[]>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getAgenciesGeographiesAgenciesGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, AgencyType[]>);
};

/**
 * Hook wrapper for getRegionsGeographiesRegionsGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetRegionsGeographiesRegionsGet = <TData = RegionType[], TError = any>(
  clientOptions: Options<GetRegionsGeographiesRegionsGetData> = {},
  queryKey?: unknown[],
  options?: Omit<UseQueryOptions<TData, TError, RegionType[]>, "queryKey" | "queryFn">
) => {
  const baseOptions = getRegionsGeographiesRegionsGetOptions(clientOptions);
  return useQuery<TData, TError, RegionType[]>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getRegionsGeographiesRegionsGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, RegionType[]>);
};

/**
 * Hook wrapper for getZonesGeographiesZonesGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetZonesGeographiesZonesGet = <TData = ZoneType[], TError = any>(
  clientOptions: Options<GetZonesGeographiesZonesGetData> = {},
  queryKey?: unknown[],
  options?: Omit<UseQueryOptions<TData, TError, ZoneType[]>, "queryKey" | "queryFn">
) => {
  const baseOptions = getZonesGeographiesZonesGetOptions(clientOptions);
  return useQuery<TData, TError, ZoneType[]>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getZonesGeographiesZonesGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, ZoneType[]>);
};

/**
 * Hook wrapper for getLegalEntityLegalEntitiesGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetLegalEntityLegalEntitiesGet = <TData = LegalEntityType[], TError = any>(
  clientOptions: Options<GetLegalEntityLegalEntitiesGetData> = {},
  queryKey?: unknown[],
  options?: Omit<UseQueryOptions<TData, TError, LegalEntityType[]>, "queryKey" | "queryFn">
) => {
  const baseOptions = getLegalEntityLegalEntitiesGetOptions(clientOptions);
  return useQuery<TData, TError, LegalEntityType[]>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getLegalEntityLegalEntitiesGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, LegalEntityType[]>);
};

/**
 * Hook wrapper for updateZoneGeographiesZonesZoneUuidPut
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useUpdateZoneGeographiesZonesZoneUuidPut = <
  TData = ZoneType,
  TError = UpdateZoneGeographiesZonesZoneUuidPutError,
  TVariables = Options<UpdateZoneGeographiesZonesZoneUuidPutData>,
>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
) => {
  const baseMutation = updateZoneGeographiesZonesZoneUuidPutMutation();
  return useMutation<TData, TError, TVariables>({
    ...baseMutation,
    ...options,
  } as UseMutationOptions<TData, TError, TVariables>);
};

/**
 * Hook wrapper for createZoneGeographiesZonesPost
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useCreateZoneGeographiesZonesPost = <
  TData = ZoneType,
  TError = CreateZoneGeographiesZonesPostError,
  TVariables = Options<CreateZoneGeographiesZonesPostData>,
>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
) => {
  const baseMutation = createZoneGeographiesZonesPostMutation();
  return useMutation<TData, TError, TVariables>({
    ...baseMutation,
    ...options,
  } as UseMutationOptions<TData, TError, TVariables>);
};

/**
 * Hook wrapper for getPostalCodesGeographiesPostalCodesGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetPostalCodesGeographiesPostalCodesGet = <TData = PostalCodeType[], TError = any>(
  clientOptions: Options<GetPostalCodesGeographiesPostalCodesGetData> = {},
  queryKey?: unknown[],
  options?: Omit<UseQueryOptions<TData, TError, PostalCodeType[]>, "queryKey" | "queryFn">
) => {
  const baseOptions = getPostalCodesGeographiesPostalCodesGetOptions(clientOptions);
  return useQuery<TData, TError, PostalCodeType[]>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getPostalCodesGeographiesPostalCodesGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, PostalCodeType[]>);
};

/**
 * Hook wrapper for getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGet = <
  TData = PaginateResourcesPostalCodeWithAgencyType,
  TError = any,
>(
  clientOptions: Options<GetPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetData> = {},
  queryKey?: unknown[],
  options?: Omit<
    UseQueryOptions<TData, TError, PaginateResourcesPostalCodeWithAgencyType>,
    "queryKey" | "queryFn"
  >
) => {
  const baseOptions =
    getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetOptions(clientOptions);
  return useQuery<TData, TError, PaginateResourcesPostalCodeWithAgencyType>({
    ...baseOptions,
    queryKey: queryKey
      ? [
          ...getPostalCodesWithBindingsGeographiesPostalCodesWithBindingsGetQueryKey(clientOptions),
          ...queryKey,
        ]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, PaginateResourcesPostalCodeWithAgencyType>);
};

/**
 * Hook wrapper for upsertPostalCodesBindingGeographiesPostalCodesBindingPut
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useUpsertPostalCodesBindingGeographiesPostalCodesBindingPut = <
  TData = any,
  TError = UpsertPostalCodesBindingGeographiesPostalCodesBindingPutError,
  TVariables = Options<UpsertPostalCodesBindingGeographiesPostalCodesBindingPutData>,
>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
) => {
  const baseMutation = upsertPostalCodesBindingGeographiesPostalCodesBindingPutMutation();
  return useMutation<TData, TError, TVariables>({
    ...baseMutation,
    ...options,
  } as UseMutationOptions<TData, TError, TVariables>);
};

/**
 * Hook wrapper for deletePostalCodesBindingGeographiesPostalCodesBindingDelete
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useDeletePostalCodesBindingGeographiesPostalCodesBindingDelete = <
  TData = any,
  TError = DeletePostalCodesBindingGeographiesPostalCodesBindingDeleteError,
  TVariables = Options<DeletePostalCodesBindingGeographiesPostalCodesBindingDeleteData>,
>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
) => {
  const baseMutation = deletePostalCodesBindingGeographiesPostalCodesBindingDeleteMutation();
  return useMutation<TData, TError, TVariables>({
    ...baseMutation,
    ...options,
  } as UseMutationOptions<TData, TError, TVariables>);
};

/**
 * Hook wrapper for previewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPost
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const usePreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPost =
  <
    TData = BindingsImportPreviewType,
    TError = PreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostError,
    TVariables =
      Options<PreviewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostData>,
  >(
    options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
  ) => {
    const baseMutation =
      previewPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportPreviewPostMutation();
    return useMutation<TData, TError, TVariables>({
      ...baseMutation,
      ...options,
    } as UseMutationOptions<TData, TError, TVariables>);
  };

/**
 * Hook wrapper for applyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPost
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPost = <
  TData = BindingsImportApplyResultType,
  TError = ApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostError,
  TVariables =
    Options<ApplyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostData>,
>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables>, "mutationFn">
) => {
  const baseMutation =
    applyPostalCodesImportGeographiesAgenciesAgencyUuidPostalCodesImportApplyPostMutation();
  return useMutation<TData, TError, TVariables>({
    ...baseMutation,
    ...options,
  } as UseMutationOptions<TData, TError, TVariables>);
};

/**
 * Hook wrapper for getExternalProvidersErrorsExternalProvidersErrorsGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetExternalProvidersErrorsExternalProvidersErrorsGet = <
  TData = PaginateResourcesExternalProviderErrorType,
  TError = any,
>(
  clientOptions: Options<GetExternalProvidersErrorsExternalProvidersErrorsGetData> = {},
  queryKey?: unknown[],
  options?: Omit<
    UseQueryOptions<TData, TError, PaginateResourcesExternalProviderErrorType>,
    "queryKey" | "queryFn"
  >
) => {
  const baseOptions = getExternalProvidersErrorsExternalProvidersErrorsGetOptions(clientOptions);
  return useQuery<TData, TError, PaginateResourcesExternalProviderErrorType>({
    ...baseOptions,
    queryKey: queryKey
      ? [
          ...getExternalProvidersErrorsExternalProvidersErrorsGetQueryKey(clientOptions),
          ...queryKey,
        ]
      : baseOptions.queryKey,
    ...options,
  } as UseQueryOptions<TData, TError, PaginateResourcesExternalProviderErrorType>);
};

/**
 * Suspense hook wrapper for getSecurityPolicyPermerSecurityPolicyGet
 * Provides compatibility with the old @7nohe/openapi-react-query-codegen API
 */
export const useGetSecurityPolicyPermerSecurityPolicyGetSuspense = <
  TData = { [key: string]: Array<string> },
  TError = any,
>(
  clientOptions: Options<GetSecurityPolicyPermerSecurityPolicyGetData> = {},
  queryKey?: unknown[],
  options?: Omit<
    UseSuspenseQueryOptions<TData, TError, { [key: string]: Array<string> }>,
    "queryKey" | "queryFn"
  >
) => {
  const baseOptions = getSecurityPolicyPermerSecurityPolicyGetOptions(clientOptions);
  return useSuspenseQuery<TData, TError, { [key: string]: Array<string> }>({
    ...baseOptions,
    queryKey: queryKey
      ? [...getSecurityPolicyPermerSecurityPolicyGetQueryKey(clientOptions), ...queryKey]
      : baseOptions.queryKey,
    ...options,
  } as UseSuspenseQueryOptions<TData, TError, { [key: string]: Array<string> }>);
};
